import { bffAuthService } from '../services/BffAuthService';
import { addCsrfToHeaders, extractCsrfTokenFromResponse, clearCachedCsrfToken } from './csrf';

interface AuthRequestInit extends RequestInit {
  headers?: Record<string, string>;
}

interface AuthFetchOptions {
  skipAuth?: boolean;
  skipCsrf?: boolean;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
}

export async function authFetch(url: string, options: AuthRequestInit = {}, authOptions: AuthFetchOptions = {}): Promise<Response> {
  const {
    skipAuth = false,
    skipCsrf = false,
    timeout = 30000,
    maxRetries = 1,
    retryDelay = 1000
  } = authOptions;

  let retryCount = 0;
  let lastError: Error | null = null;

  while (retryCount <= maxRetries) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (!skipCsrf) {
      addCsrfToHeaders(headers);
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include'
    };

    if (options.body instanceof FormData) {
      delete (requestOptions.headers as any)['Content-Type'];
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      extractCsrfTokenFromResponse(response);

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : Math.min(5000 * (retryCount + 1), 30000);

        console.warn(`authFetch - Rate limited (429), waiting ${waitTime}ms before retry (${retryCount + 1}/${maxRetries}):`, url);

        if (retryCount < maxRetries) {
          retryCount++;
          lastError = new Error('HTTP 429 Too Many Requests');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }

        return response;
      }

      if (response.status === 401 && !skipAuth) {
        // 先尝试静默续期：检查BFF Session是否仍然有效
        // 如果有效（access token已刷新），重试原始请求
        if (retryCount < maxRetries) {
          const reauthed = await bffAuthService.trySilentReauth();
          if (reauthed) {
            retryCount++;
            continue; // 重试原始请求
          }
        }
        // Session确实过期，handleAuthLost已在trySilentReauth中调用
        if (!bffAuthService.isAuthenticated) {
          // trySilentReauth已经调用了handleAuthLost
        } else {
          bffAuthService.handleAuthLost();
        }
        return response;
      }

      if (response.status === 403 && !skipAuth && !skipCsrf && retryCount < maxRetries) {
        console.warn(`authFetch - 403 error, clearing CSRF token and retrying (${retryCount + 1}/${maxRetries}):`, url);

        clearCachedCsrfToken();

        retryCount++;
        lastError = new Error('HTTP 403 Forbidden');
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        lastError = new Error(`Request timeout (${timeout}ms)`);
      } else {
        lastError = error as Error;
      }

      if (retryCount < maxRetries) {
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      throw lastError;
    }
  }

  throw lastError || new Error('Request failed');
}

export async function authGet(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'GET' }, authOptions);
}

export async function authPost(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'POST' }, authOptions);
}

export async function authPut(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'PUT' }, authOptions);
}

export async function authDelete(url: string, options: AuthRequestInit = {}, authOptions?: AuthFetchOptions): Promise<Response> {
  return authFetch(url, { ...options, method: 'DELETE' }, authOptions);
}
